/**
 * "Auto-Translate to DE" button. Asks UILM to AI-translate every key in
 * the template's module from `en-US` into the other supported locales.
 *
 * UILM is async: `Key/TranslateAll` returns 200 the moment the job is
 * queued, not when translations are ready. The actual completion signal
 * comes from a server-side webhook (handled outside this app) that
 * patches the `UilmTranslationJob` UDS row this button just inserted.
 *
 * Local responsibilities here:
 *   1. Generate a `messageCoRelationId` UUID (the join key the webhook
 *      receiver will use to find the row).
 *   2. Insert a `UilmTranslationJob` row with `Status: 'running'`.
 *   3. Call `translateAll` with that same UUID.
 *   4. If `translateAll` throws (UILM rejected the kickoff), patch the
 *      row to `Status: 'failed'` so the banner explains why.
 *   5. Notify the parent so it can kick its diff/banner refetch — the
 *      `useLatestTranslationJob` hook upstream picks up the new row and
 *      starts polling for completion.
 */

import { useState } from 'react';
import { Languages, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { DropdownMenuItem } from '@/components/ui-kit/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { DEFAULT_LANGUAGE_CULTURE } from '../../lib/uilm-constants';
import { translateAll } from '../../services/uilm-admin.service';
import {
  createTranslationJob,
  updateTranslationJob,
} from '../../services/uilm-translation-job.service';

interface TranslationTabTranslateActionProps {
  moduleId: string;
  templateId: string;
  templateSlug: string;
  /**
   * If there are no keys on UILM yet, auto-translate has nothing to chew
   * on — we disable the button until Seed runs first.
   */
  hasRemoteKeys: boolean;
  /**
   * `true` while UILM has an in-flight translate job for this template
   * (the latest `UilmTranslationJob` UDS row is still `running`). Reflects
   * server-side state coming from `useLatestTranslationJob`, so the button
   * stays disabled even if the admin refreshes the page mid-job — they
   * can't kick off a duplicate run.
   */
  isJobRunning?: boolean;
  /**
   * Fired after the job row is inserted (and `translateAll` returned)
   * so the parent can refetch the latest-job hook and show the banner
   * without waiting for the next poll tick.
   */
  onJobStarted?: () => void;
  /** When true, renders as a DropdownMenuItem (used inside the mobile
      three-dot menu) instead of the default standalone Button. */
  asMenuItem?: boolean;
}

export function TranslationTabTranslateAction({
  moduleId,
  templateId,
  templateSlug,
  hasRemoteKeys,
  isJobRunning = false,
  onJobStarted,
  asMenuItem = false,
}: TranslationTabTranslateActionProps) {
  const { toast } = useToast();
  const [running, setRunning] = useState(false);

  const handleTranslate = async () => {
    setRunning(true);
    const messageCoRelationId = crypto.randomUUID();
    const startedAt = new Date().toISOString();
    let jobItemId: string | null = null;

    try {
      // 1. Record the job in UDS first. If this fails we never call
      //    UILM — keeps history consistent.
      jobItemId = await createTranslationJob({
        MessageCoRelationId: messageCoRelationId,
        TemplateId: templateId,
        TemplateSlug: templateSlug,
        ModuleId: moduleId,
        DefaultLanguage: DEFAULT_LANGUAGE_CULTURE,
        Status: 'running',
        StartedAt: startedAt,
      });

      // 2. Kick UILM off using the same correlation id so the webhook
      //    receiver can match the eventual completion event back to
      //    this row.
      await translateAll(moduleId, DEFAULT_LANGUAGE_CULTURE, messageCoRelationId);

      toast({
        variant: 'success',
        title: 'Translation queued',
        description:
          'UILM is translating to DE in the background. The status banner will update when it finishes.',
      });
      onJobStarted?.();
    } catch (err) {
      console.error('Translate-all failed:', err);
      // If we managed to insert the row but UILM rejected the call,
      // mark the row as failed so the banner shows the reason — otherwise
      // it would sit in `running` forever (the webhook will never fire
      // because the job never started server-side).
      if (jobItemId) {
        try {
          await updateTranslationJob(jobItemId, {
            Status: 'failed',
            CompletedAt: new Date().toISOString(),
            ErrorMessage: err instanceof Error ? err.message : 'translateAll rejected',
          });
        } catch (patchErr) {
          console.warn('Failed to mark translation job as failed:', patchErr);
        }
      }
      toast({
        variant: 'destructive',
        title: 'Translation failed',
        description: err instanceof Error ? err.message : 'Could not queue the translation job.',
      });
      onJobStarted?.();
    } finally {
      setRunning(false);
    }
  };

  // Three states drive the button's appearance:
  //   - `running`       local click in flight (insert UDS row → call UILM)
  //   - `isJobRunning`  server-side UILM job still working (from polling)
  //   - idle            no job, ready to kick off
  // Spinner-with-text wins when either of the first two are true; the
  // button is disabled in both so the admin can't double-fire.
  const busy = running || isJobRunning;
  const busyLabel = running ? 'Queuing…' : 'Translating…';
  const disabled = busy || !hasRemoteKeys;
  const icon = busy ? (
    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
  ) : (
    <Languages className="h-4 w-4 mr-2" />
  );
  const label = busy ? busyLabel : 'Auto-Translate to DE';

  if (asMenuItem) {
    return (
      <DropdownMenuItem
        disabled={disabled}
        onSelect={(e) => {
          e.preventDefault();
          handleTranslate();
        }}
      >
        {icon}
        {label}
      </DropdownMenuItem>
    );
  }

  return (
    <Button onClick={handleTranslate} disabled={disabled} variant="primaryOutline" size="sm">
      {icon}
      {label}
    </Button>
  );
}

// {
//     "TranslateAllEvent": {
//       "ModuleId": null,
//       "MessageCoRelationId": "2d878286-89b6-4ee0-9845-0d54d8dc8f2b",
//       "ProjectKey": "S4e55b6bc5640407caf9a6c61b50afe45",
//       "DefaultLanguage": "en-US"
//     },
//     "Response": true
//   }

// {{json.output.TranslateAllEvent.MessageCoRelationId}}
